import { useCallback } from 'react';
import {
  type EnterpriseMembers,
  type ProductAccess
} from '../../common/utils/billing-client/client-v2';
import { useProducts } from '../../common/contexts/products';
import { useEnterprises } from '../../common/contexts/enterprises';
import { getHiddenProductSlugsFromSubs } from '../utils/get-hidden-product-slugs-from-subs';
import { useFeatureFlags } from '../../common/contexts/featureFlags';
import type { ProductSlugs } from '@deque/billing-utils';

const useCSVExport = () => {
  const { getProductBySlug } = useProducts();
  const { activeEnterprise } = useEnterprises();
  const { featureFlags } = useFeatureFlags();
  const hiddenProductSlugs = getHiddenProductSlugsFromSubs(
    activeEnterprise?.subscriptions || [],
    getProductBySlug,
    featureFlags
  );

  const processData = useCallback((data, headers = []) => {
    if (!data || !data.length) {
      return '';
    }

    // If headers aren't provided, use object keys from first data item
    const csvHeaders = headers.length > 0 ? headers : Object.keys(data[0]);

    // Create CSV header row
    const headerRow = csvHeaders.join(',');

    // Create data rows
    const csvRows = data.map(
      (
        item: {
          [key: string]: unknown;
        } & EnterpriseMembers.PendingOrAcceptedMember
      ) => {
        return csvHeaders
          .map((header: string) => {
            if (header === 'product_access') {
              const productAccess = item.product_access
                .map((product: ProductAccess) => {
                  if (
                    hiddenProductSlugs.includes(
                      product.product_slug as ProductSlugs
                    )
                  ) {
                    return '';
                  }

                  return `${
                    getProductBySlug(product.product_slug)?.name
                  } (${product.access_type.toLowerCase()})`;
                })
                .filter(access => access !== '');

              productAccess.unshift(
                `axe Account (${item.is_admin ? 'admin' : 'general'})`
              );

              return productAccess.join(' | ');
            }

            if (header === 'status') {
              return 'user_id' in item ? 'Active' : 'Email sent';
            }

            // Handle fields that contain commas or quotes
            let field = item[header]?.toString() ?? '';
            if (
              field.includes(',') ||
              field.includes('"') ||
              field.includes('\n')
            ) {
              field = `"${field.replace(/"/g, '""')}"`;
            }
            return field;
          })
          .join(',');
      }
    );

    // Combine headers and data
    return [headerRow, ...csvRows].join('\n');
  }, []);

  return { processData };
};

export default useCSVExport;
